import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, message, Space, Table, Tooltip } from 'antd';
import { FilePdfOutlined, SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import 'moment/locale/es';

import client from '../../../../api/api';
import toCurrency from '../../../../libs/currencyUtils';
import useSearchParams from '../../../Global/Antd/Table/useSearchParams';
import SearchColumn from '../../../Global/Antd/Table/searchColumn';
import EmptyRecord from '../EmptyRecord';
import SignedReport from '../SignedReport';
import GasoilModal from '../GasoilModal';
import './carrier-record-table.css';

const locationCellStyle = {
  display: 'block',
  whiteSpace: 'normal',
  wordWrap: 'break-word',
};

const getLocationLabel = (location, province) =>
  province ? `${location || ''}, ${province}` : location || '';

const formatDate = value =>
  value
    ? moment(value)
        .locale('es')
        .format('DD-MMM-YYYY')
    : '';

const DateSearchColumn = (columnName, handleSearch, handleReset) => ({
  // eslint-disable-next-line react/prop-types
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <DatePicker
        format="DD-MM-YYYY"
        value={selectedKeys[0] ? moment(selectedKeys[0], 'YYYY-MM-DD') : null}
        onChange={date => setSelectedKeys(date ? [date.format('YYYY-MM-DD')] : [])}
        style={{ marginBottom: 8, display: 'block', width: '100%' }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys[0], confirm, columnName)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Buscar
        </Button>
        <Button onClick={() => handleReset(columnName, clearFilters)} size="small" style={{ width: 90 }}>
          Limpiar
        </Button>
      </Space>
    </div>
  ),
  filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
});

const CarrierRecordTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [paginationConfiguration, setPaginationConfiguration] = useState();
  const [order, setOrder] = useState({});
  const [searchParams, setSearchParams] = useState([]);
  const [paginationParams, setPaginationParams] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const [queryParams, handleColumnSearch, handleResetColumnSearch] = useSearchParams(
    searchParams,
    setSearchParams
  );
  const [openModal, setOpenModal] = useState(false);
  const [typeContentModal, setTypeContentModal] = useState('');
  const [historyItem, setHistoryItem] = useState();

  const openCpe = async record => {
    setPdfLoadingId(record.id);
    try {
      const res = await client.endpoints.generic.getReports(record.waybillPdf || record.signedReport);
      const pdfWindow = window.open('');
      const reader = new FileReader();
      reader.readAsDataURL(res.data);
      reader.onloadend = () => {
        const base64data = reader.result;
        if (pdfWindow) {
          pdfWindow.document.write(
            `<iframe width='100%' height='100%' src='${base64data}'></iframe>`
          );
        }
      };
      reader.onerror = () => {
        message.error('Hubo un error al abrir el archivo');
      };
    } catch (error) {
      message.error('Hubo un error al abrir el archivo');
    } finally {
      setPdfLoadingId(null);
    }
  };

  const openModalHandle = (modalType, record) => {
    setTypeContentModal(modalType);
    setHistoryItem(record);
    setOpenModal(true);
  };

  const canUploadEndPicture = status =>
    ['pendienteFotoFin', 'errorFotoFin', 'completa', 'finalizada'].includes(status);

  const columns = [
    {
      title: 'Foto inicio de viaje',
      key: 'originPicture',
      align: 'center',
      width: 80,
      render: record => <EmptyRecord shipment={record} />,
    },
    {
      title: 'Foto fin de viaje',
      key: 'endPicture',
      align: 'center',
      width: 80,
      render: record => (canUploadEndPicture(record?.status) ? <SignedReport shipment={record} /> : null),
    },
    {
      title: 'Ver órdenes emitidas',
      key: 'viewGasOrders',
      align: 'center',
      width: 90,
      render: record =>
        record?.gasOrders?.length > 0 ? (
          <Tooltip title="Ver órdenes emitidas">
            <button
              className="gasoil-list"
              type="button"
              onClick={() => openModalHandle('gasOrderList', record)}
            >
              <img src="/images/icons/gasoil_list.png" alt="Ver órdenes emitidas" />
            </button>
          </Tooltip>
        ) : (
          <button className="gasoil-list" type="button">
            <img src="/images/icons/gasoil_list_disabled.png" alt="Sin órdenes emitidas" />
          </button>
        ),
    },
    {
      title: 'Ver CPE',
      key: 'cpe',
      align: 'center',
      width: 80,
      render: record =>
        record?.tripDocument ? (
          <Tooltip title="Ver CPE">
            <Button
              type="text"
              icon={<FilePdfOutlined style={{ color: '#6495ed', fontSize: 20 }} />}
              loading={pdfLoadingId === record.id}
              onClick={() => openCpe(record)}
            />
          </Tooltip>
        ) : null,
    },
    {
      title: 'CTG',
      key: 'tripDocument',
      align: 'center',
      width: 100,
      render: record => record?.tripDocument,
      ...SearchColumn('tripDocument', handleColumnSearch, handleResetColumnSearch),
    },
    {
      title: 'Fecha del Viaje',
      key: 'loadDate',
      render: record => formatDate(record?.load?.date),
      ...DateSearchColumn('loadDate', handleColumnSearch, handleResetColumnSearch),
    },
    {
      title: 'Origen',
      key: 'fromCity',
      render: record => {
        const value = getLocationLabel(record?.load?.from, record?.load?.fromProvince);
        return (
          <Tooltip title={value}>
            <span style={locationCellStyle}>{record?.load?.from}</span>
          </Tooltip>
        );
      },
      ...SearchColumn('fromCity', handleColumnSearch, handleResetColumnSearch),
    },
    {
      title: 'Destino',
      key: 'toCity',
      render: record => {
        const value = getLocationLabel(record?.load?.to, record?.load?.toProvince);
        return (
          <Tooltip title={value}>
            <span style={locationCellStyle}>{record?.load?.to}</span>
          </Tooltip>
        );
      },
      ...SearchColumn('toCity', handleColumnSearch, handleResetColumnSearch),
    },
    {
      title: 'Producto',
      key: 'productType',
      render: record => record?.load?.product?.name,
      ...SearchColumn('productType', handleColumnSearch, handleResetColumnSearch),
    },
    {
      title: 'Tarifa',
      key: 'transportFare',
      render: record =>
        record.transportFare === null
          ? toCurrency(record?.load?.transportFare)
          : toCurrency(record?.transportFare),
    },
    {
      title: 'Camionero',
      key: 'truckerName',
      sorter: true,
      render: record => record?.trucker?.user?.legalName,
      ...SearchColumn('truckerName', handleColumnSearch, handleResetColumnSearch),
    },
  ];

  const fetchShipments = useCallback(async () => {
    try {
      setLoading(true);
      const filters = queryParams(order, paginationParams);
      const resp = await client.endpoints.carrier.getCarrierActiveShipments(filters);
      setData(resp.data.rows);
      setPaginationConfiguration({
        position: ['bottomLeft'],
        total: resp.data.count,
        showTotal: total => `Total de ${total} ítems`,
        pageSize: paginationParams.pageSize,
        pageSizeOptions: ['10', '20', '30', '40', '50'],
        current: paginationParams.currentPage,
        showSizeChanger: true,
      });
    } catch (error) {
      console.error(error);
      message.error('Error al buscar viajes activos');
    } finally {
      setLoading(false);
    }
    // queryParams se recrea en cada render; searchParams es la dependencia real
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, paginationParams, searchParams]);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const handleTableChange = (pagination, _filters, sorter) => {
    if (sorter.column) {
      setOrder({
        sorting: `${sorter.columnKey}:${sorter.order === 'ascend' ? 'asc' : 'desc'},`,
      });
    } else {
      setOrder({});
    }
    setPaginationParams({
      currentPage: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  return (
    <>
      <div className="carrier-record-table-wrapper">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={paginationConfiguration}
          onChange={handleTableChange}
          rowClassName={record => (record.status === 'cancelada' ? 'cancelled-shipment' : '')}
          scroll={{ x: 'max-content' }}
        />
      </div>
      <GasoilModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        typeContentModal={typeContentModal}
        historyItem={historyItem}
      />
    </>
  );
};

export default CarrierRecordTable;
